import React from 'react';
import PageHeader from '@/components/shared/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  KPI_MOCK,
  RECENT_DOCUMENTS,
  netProfit,
  QUICK_ACCOUNTS_MOCK,
  type RecentDocument,
} from '@/lib/mock/dashboard';
import { formatRial } from '@/lib/formatMoney';
import { type ColumnDef } from '@tanstack/react-table';
import DataTable from '@/components/shared/DataTable';

const FinanceDashboard: React.FC = () => {
  const kpis = KPI_MOCK;
  const profit = netProfit(kpis);
  const quickRows = QUICK_ACCOUNTS_MOCK;

  const docColumns = React.useMemo<ColumnDef<RecentDocument>[]>(
    () => [
      {
        accessorKey: 'number',
        header: 'شماره سند',
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.number}</span>,
      },
      { accessorKey: 'dateFa', header: 'تاریخ', cell: ({ row }) => row.original.dateFa },
      { accessorKey: 'description', header: 'شرح', cell: ({ row }) => row.original.description },
      {
        accessorKey: 'debit',
        header: () => <span dir="ltr">Debit</span>,
        cell: ({ row }) => (
          <span className="font-mono">{formatRial(row.original.debit)}</span>
        ),
      },
      {
        accessorKey: 'credit',
        header: () => <span dir="ltr">Credit</span>,
        cell: ({ row }) => (
          <span className="font-mono">{formatRial(row.original.credit)}</span>
        ),
      },
      { accessorKey: 'status', header: 'وضعیت' },
    ],
    [],
  );


  type KpiCard = {
    title: string;
    value: number;
    tint: string;
    border: string;
    chip: string;
    accent?: string;
  };

  const cards: KpiCard[] = [
    {
      title: 'مجموع دارایی‌ها',
      value: kpis.total_assets,
      tint: 'from-amber-500/20 via-amber-500/5 to-transparent',
      border: 'border-amber-300/80',
      chip: 'bg-amber-100 text-amber-800',
    },
    {
      title: 'مجموع بدهی‌ها',
      value: kpis.total_liabilities,
      tint: 'from-rose-500/20 via-rose-500/5 to-transparent',
      border: 'border-rose-300/80',
      chip: 'bg-rose-100 text-rose-800',
    },
    {
      title: 'کل درآمد عملیاتی',
      value: kpis.total_revenue,
      tint: 'from-emerald-500/20 via-emerald-500/5 to-transparent',
      border: 'border-emerald-300/80',
      chip: 'bg-emerald-100 text-emerald-800',
    },
    {
      title: 'کل هزینه‌ها',
      value: kpis.total_expenses,
      tint: 'from-orange-500/20 via-orange-500/5 to-transparent',
      border: 'border-orange-300/80',
      chip: 'bg-orange-100 text-orange-800',
    },
    {
      title: 'سود (زیان) خالص تشخیصی',
      value: profit,
      tint:
        profit >= 0
          ? 'from-sky-500/20 via-sky-500/5 to-transparent'
          : 'from-violet-500/20 via-violet-500/5 to-transparent',
      border: profit >= 0 ? 'border-sky-300/80' : 'border-violet-300/80',
      chip: profit >= 0 ? 'bg-sky-100 text-sky-800' : 'bg-violet-100 text-violet-800',
      accent: profit >= 0 ? 'text-emerald-700' : 'text-rose-700',
    },
  ];

  return (
    <div className="w-full min-w-0 space-y-6 rounded-2xl bg-gradient-to-b from-slate-50 to-white p-3 sm:p-5">
      <PageHeader
        title="داشبورد مالی"
        description="نمای تجمیعی بر اساس دادهٔ موقت پیش‌طرح؛ اتصالی به سرور واقعی نیست."
        actions={
          <span className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-[11px] text-slate-600 shadow-sm">
            واحد اندازه‌گیری پیش‌طرح — ریال
          </span>
        }
      />

      <div className="grid w-full min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {cards.map((c, i) => (
          <Card
            key={i}
            className={`border bg-white bg-gradient-to-br ${c.tint} ${c.border} shadow-sm transition hover:-translate-y-0.5 hover:shadow-md`}
          >
            <CardHeader className="pb-2">
              <div className="mb-2 flex justify-start">
                <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${c.chip}`}>
                  گزارش لحظه‌ای
                </span>
              </div>
              <CardDescription className="text-xs">{c.title}</CardDescription>
            </CardHeader>
            <CardContent>
              <p
                className={`text-xl font-bold tabular-nums md:text-2xl ${c.accent ?? 'text-slate-800'}`}
              >
                {formatRial(c.value)}{' '}
                <span className="text-sm font-normal text-muted-foreground">ریال</span>
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid w-full min-w-0 grid-cols-1 gap-6 lg:grid-cols-12">
        <Card className="min-w-0 overflow-hidden border border-slate-200 bg-white shadow-sm lg:col-span-7">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">اخیراً ثبت‌شده‌ها</CardTitle>
            <CardDescription>اسناد نمونه دفتر روزنامه (قطعی یا موقت)</CardDescription>
          </CardHeader>
          <CardContent className="min-w-0 px-0 pb-6 pt-0 sm:px-2">
            <DataTable columns={docColumns} data={RECENT_DOCUMENTS} />
          </CardContent>
        </Card>

        <div className="flex min-w-0 flex-col gap-4 lg:col-span-5">
          <Card className="min-h-[12rem] border border-indigo-200/70 bg-gradient-to-br from-indigo-50 via-cyan-50 to-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">نمودار دید کلان</CardTitle>
              <CardDescription>جای گراف؛ در مرحله اتصال دادهٔ واقعی درآمد به‌صورت ماهانه</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex h-[11rem] items-end gap-3 rounded-xl border border-indigo-200/70 bg-white/80 p-4">
                {[45, 70, 40, 90, 55, 80, 62].map((h, i) => (
                  <div
                    key={i}
                    style={{ height: `${h}%` }}
                    className="flex flex-1 items-end rounded-t-lg bg-gradient-to-t from-indigo-600/85 via-sky-500/70 to-cyan-300/70"
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border border-slate-200 bg-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">خلاصه سریع — حساب‌های جزئی پیش‌طرح</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                {quickRows.map((r) => (
                  <li
                    key={r.code}
                    className="flex items-center justify-between gap-4 rounded-md border border-transparent px-2 py-2 transition hover:border-slate-200 hover:bg-slate-50/80"
                  >
                    <span className="min-w-0 truncate text-muted-foreground">
                      <span className="font-mono text-xs">{r.code}</span> {r.name}
                    </span>
                    <span
                      className={`shrink-0 font-mono font-semibold tabular-nums ${r.balance < 0 ? 'text-rose-700' : 'text-emerald-800'}`}
                    >
                      {formatRial(r.balance)}
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

    </div>
  );
};

export default FinanceDashboard;
